import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FeedPostActions from './FeedPostActions';
import FeedCommentList from './FeedCommentList';
import FeedSignBadge from './FeedSignBadge';
import FeedPostImage from './FeedPostImage';
import FeedImageViewer from './FeedImageViewer';
import useFeedComments from '../hooks/useFeedComments';
import { formatRelativeDate, resolveAvatarUrl } from '../utils/formatDate';
import { localizedValue } from '../localization/presentationLanguage';






function getInitials(post) {
  const author = post?.author || {};
  const base = String(author?.fullName || author?.username || 'Z').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return base.charAt(0).toUpperCase() || 'Z';
}

function hashString(value = '') {
  let hash = 0;
  const str = String(value || 'zodiacus');
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getAvatarPalette(seed = '') {
  const palettes = [
    { from: '#6D5DF6', to: '#8B5CF6', accent: 'rgba(255,255,255,0.24)' },
    { from: '#F59E0B', to: '#F97316', accent: 'rgba(255,248,220,0.24)' },
    { from: '#10B981', to: '#06B6D4', accent: 'rgba(255,255,255,0.22)' },
    { from: '#EC4899', to: '#8B5CF6', accent: 'rgba(255,240,250,0.24)' },
    { from: '#F43F5E', to: '#FB7185', accent: 'rgba(255,245,245,0.22)' },
    { from: '#0EA5E9', to: '#2563EB', accent: 'rgba(255,255,255,0.24)' },
    { from: '#14B8A6', to: '#0F766E', accent: 'rgba(240,255,250,0.22)' },
    { from: '#A855F7', to: '#6366F1', accent: 'rgba(250,245,255,0.24)' },
  ];

  return palettes[hashString(seed) % palettes.length];
}



function isSamePostId(a, b) {
  return String(a || '').trim() !== '' && String(a || '').trim() === String(b || '').trim();
}

function sanitizeSystemText(text = '') {
  return String(text)
    .split('\n')
    .filter((line) => {
      const l = line.trim().toLowerCase();
      return !(
        l.startsWith('#seed:') ||
        l.startsWith('#seedcomment:') ||
        l.startsWith('#livefeedpost:') ||
        l.startsWith('#livefeedcomment:') ||
        l.startsWith('#liveforumthread:') ||
        l.startsWith('#liveforumreply:') ||
        l.startsWith('#seedthread') ||
        l.startsWith('#seedreply')
      );
    })
    .join('\n')
    .trim();
}

export default function FeedPostCard({
  post,
  onLike,
  onDelete,
  onOpenThread,
  showInlineComments = true,
  isThreadView = false,
  initiallyExpanded = false,
  deepLinkedPostId = '',
  isRecentlyApplied = false,
  className = '',
}) {
  const navigateTo = useNavigate();
  const [commentsOpen, setCommentsOpen] = useState(!!initiallyExpanded);
  const [deleting, setDeleting] = useState(false);
  const [liking, setLiking] = useState(false);
  const [isMountedIn, setIsMountedIn] = useState(false);
  const [isDeepLinked, setIsDeepLinked] = useState(false);
  const [isRecentlyAppliedFx, setIsRecentlyAppliedFx] = useState(false);
  const [isCardPressed, setIsCardPressed] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const cardRef = useRef(null);
  const mountedInTimeoutRef = useRef(null);
  const viewerCleanupTimeoutRef = useRef(null);

  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === 'undefined') return 1280;
    return window.innerWidth || 1280;
  });


  const safePost = useMemo(() => {
    const base = post || {};
    return {
      ...base,
      text: sanitizeSystemText(base?.text || ''),
      content: sanitizeSystemText(base?.content || ''),
    };
  }, [post]);
  const author = useMemo(() => safePost.author || {}, [safePost]);
  const avatarUrl = useMemo(() => resolveAvatarUrl(author), [author]);
  const avatarPalette = useMemo(
    () => getAvatarPalette(author?.username || author?.fullName || safePost?.id || 'zodiacus'),
    [author, safePost?.id]
  );
  const isOptimistic = !!safePost?._optimistic;
  const isGlowActive = isDeepLinked || isRecentlyAppliedFx;
  const isInteractiveGlow = isCardHovered || isCardPressed;
  const feedOrder = Number(safePost?._feedOrder ?? safePost?._index ?? 0);
  const cappedFeedOrder = Number.isFinite(feedOrder) ? Math.max(0, Math.min(feedOrder, 7)) : 0;
  const mountDelayMs = isThreadView ? 0 : cappedFeedOrder * 34;
  const interactiveLift = isCardPressed ? 0 : isCardHovered ? -3 : 0;

  const isMobile = viewportWidth <= 640;

  const hasImageMedia = useMemo(() => {
    return (
      !!safePost?.hasMedia &&
      safePost?.media?.kind === 'image' &&
      !!String(safePost?.media?.image?.url || '').trim()
    );
  }, [safePost]);

  const handleOpenImageViewer = useCallback(({ image } = {}) => {
    const safeUrl = String(image?.url || image?.thumbUrl || '').trim();
    if (!safeUrl) return;

    if (viewerCleanupTimeoutRef.current && typeof window !== 'undefined') {
      window.clearTimeout(viewerCleanupTimeoutRef.current);
      viewerCleanupTimeoutRef.current = null;
    }

    setViewerImage({
      url: safeUrl,
      thumbUrl: String(image?.thumbUrl || '').trim(),
      alt: String(image?.alt || safePost?.text || 'Imagen del post').trim(),
    });
    setViewerOpen(true);
  }, [safePost?.text]);

  const handleCloseImageViewer = useCallback(() => {
    setViewerOpen(false);

    if (typeof window === 'undefined') {
      setViewerImage(null);
      return;
    }

    if (viewerCleanupTimeoutRef.current) {
      window.clearTimeout(viewerCleanupTimeoutRef.current);
      viewerCleanupTimeoutRef.current = null;
    }

    viewerCleanupTimeoutRef.current = window.setTimeout(() => {
      setViewerImage(null);
      viewerCleanupTimeoutRef.current = null;
    }, 180);
  }, []);

  useEffect(() => {
    return () => {
      if (viewerCleanupTimeoutRef.current) {
        window.clearTimeout(viewerCleanupTimeoutRef.current);
        viewerCleanupTimeoutRef.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setViewportWidth(window.innerWidth || 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsMountedIn(true);
      return undefined;
    }

    let rafId = 0;
    mountedInTimeoutRef.current = window.setTimeout(() => {
      rafId = window.requestAnimationFrame(() => setIsMountedIn(true));
    }, mountDelayMs);

    return () => {
      if (mountedInTimeoutRef.current) {
        window.clearTimeout(mountedInTimeoutRef.current);
        mountedInTimeoutRef.current = null;
      }
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [mountDelayMs]);



  const postId = String(safePost.id || '');
  const authorProfileId = String(author?._id || author?.id || author?.userId || '').trim();
  
  const matchesDeepLink = useMemo(
    () => isSamePostId(postId, deepLinkedPostId),
    [postId, deepLinkedPostId]
  );

  useEffect(() => {
    if (!matchesDeepLink) return undefined;

    setIsDeepLinked(true);
    setCommentsOpen(true);

    const scrollTimeout = window.setTimeout(() => {
      if (cardRef.current?.scrollIntoView) {
        cardRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 160);

    const glowTimeout = window.setTimeout(() => {
      setIsDeepLinked(false);
    }, 3600);

    return () => {
      window.clearTimeout(scrollTimeout);
      window.clearTimeout(glowTimeout);
    };
  }, [matchesDeepLink]);

  useEffect(() => {
    if (!isRecentlyApplied) return undefined;

    setIsRecentlyAppliedFx(true);

    const timeout = window.setTimeout(() => {
      setIsRecentlyAppliedFx(false);
    }, 3200);

    return () => window.clearTimeout(timeout);
  }, [isRecentlyApplied]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePointerUp = () => {
      setIsCardPressed(false);
    };

    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    window.addEventListener('blur', handlePointerUp);

    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      window.removeEventListener('blur', handlePointerUp);
    };
  }, []);


  const {
    items: rawComments,
    loading: commentsLoading,
    creating: commentsCreating,
    error: commentsError,
    submitComment,
    removeComment,
    toggleLikeComment,
    submitCommentReport,
  } = useFeedComments({
    postId,
    autoLoad: commentsOpen,
  });

  const comments = useMemo(() => {
    if (!Array.isArray(rawComments)) return [];
    return rawComments.map((comment) => ({
      ...comment,
      text: sanitizeSystemText(comment?.text || ''),
      content: sanitizeSystemText(comment?.content || ''),
      body: sanitizeSystemText(comment?.body || ''),
      replies: Array.isArray(comment?.replies)
        ? comment.replies.map((reply) => ({
            ...reply,
            text: sanitizeSystemText(reply?.text || ''),
            content: sanitizeSystemText(reply?.content || ''),
            body: sanitizeSystemText(reply?.body || ''),
          }))
        : comment?.replies,
    }));
  }, [rawComments]);

  const displayedCommentCount = useMemo(() => {
    const apiCount = Number(safePost?.stats?.commentCount || 0);
    const loadedCount = Array.isArray(comments) ? comments.length : 0;
    return Math.max(apiCount, loadedCount);
  }, [safePost, comments]);
  const handleOpenThread = useCallback(() => {
    if (viewerOpen) return;

    if (typeof onOpenThread === 'function') {
      onOpenThread(safePost);
      return;
    }

    setCommentsOpen((prev) => !prev);
  }, [onOpenThread, safePost, viewerOpen]);

  const handleToggleComments = useCallback(async () => {
    if (!showInlineComments && !commentsOpen) {
      handleOpenThread();
      return;
    }

    setCommentsOpen((prev) => !prev);
  }, [commentsOpen, handleOpenThread, showInlineComments]);

  const handleLike = useCallback(async () => {
    if (typeof onLike !== 'function' || liking || deleting) return;

    try {
      setLiking(true);
      await onLike(safePost);
    } finally {
      setLiking(false);
    }
  }, [onLike, liking, deleting, safePost]);

  const handleDelete = useCallback(async () => {
    if (typeof onDelete !== 'function' || deleting || !safePost?.viewer?.isOwner) return;

    try {
      setDeleting(true);
      await onDelete(safePost);
    } finally {
      setDeleting(false);
    }
  }, [onDelete, deleting, safePost]);

  const handleSubmitComment = useCallback(async ({ text }) => {
    const result = await submitComment({ text });
    if (!commentsOpen) {
      setCommentsOpen(true);
    }
    return result;
  }, [submitComment, commentsOpen]);

  const handleDeleteComment = useCallback(async (comment) => {
    const commentId = String(comment?.id || '');
    if (!commentId) return;
    await removeComment(commentId);
  }, [removeComment]);

  const handleLikeComment = useCallback(async (comment) => {
    const commentId = String(comment?.id || '');
    if (!commentId) return;
    await toggleLikeComment(commentId);
  }, [toggleLikeComment]);

  const handleReportComment = useCallback(async (comment) => {
    const commentId = String(comment?.commentId || comment?.id || '');
    if (!commentId) return;
    await submitCommentReport(commentId, {
      reason: String(comment?.reason || 'other').trim() || 'other',
      details: String(comment?.details || '').trim(),
    });
  }, [submitCommentReport]);

  const handleOpenAuthorProfile = useCallback((event) => {
    event?.stopPropagation?.();

    if (viewerOpen || !authorProfileId) return;

    navigateTo(`/profile/${authorProfileId}`);
  }, [authorProfileId, navigateTo, viewerOpen]);

  return (
    <article
      ref={cardRef}
      className={`feed-post-card ${isOptimistic ? 'is-optimistic' : ''} ${isMountedIn ? 'is-mounted-in' : ''} ${isDeepLinked ? 'is-deep-linked' : ''} ${isRecentlyAppliedFx ? 'is-recently-applied' : ''} ${isCardHovered ? 'is-hovered' : ''} ${isCardPressed ? 'is-pressed' : ''} ${isThreadView && commentsOpen ? 'is-thread-focus-open' : ''} ${className}`.trim()}
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => {
        setIsCardHovered(false);
        setIsCardPressed(false);
      }}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse' && isMobile) return;
        setIsCardPressed(true);
      }}
      style={{
        position: 'relative',
        isolation: 'isolate',
        overflow: 'visible',
        borderRadius: isMobile ? 20 : 26,
        border: isGlowActive
          ? isDeepLinked
            ? '1px solid rgba(158,118,255,0.24)'
            : '1px solid rgba(255,224,130,0.20)'
          : isOptimistic
            ? '1px solid rgba(255,224,130,0.18)'
            : '1px solid rgba(255,255,255,0.08)',
        background:
          isMobile
            ? 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))'
            : 'linear-gradient(180deg, rgba(255,255,255,0.11), rgba(255,255,255,0.045))',
        boxShadow: isGlowActive
          ? isDeepLinked
            ? '0 22px 48px rgba(8,5,20,0.22), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 42px rgba(158,118,255,0.12), 0 0 20px rgba(255,224,130,0.08)'
            : '0 22px 48px rgba(8,5,20,0.22), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 36px rgba(255,224,130,0.12), 0 0 18px rgba(255,234,180,0.08)'
          : isInteractiveGlow
            ? isCardPressed
              ? '0 14px 30px rgba(8,5,20,0.16), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 22px rgba(255,224,130,0.06)'
              : '0 24px 52px rgba(8,5,20,0.22), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 34px rgba(255,224,130,0.09), 0 0 18px rgba(170,124,255,0.06)'
            : isOptimistic
              ? '0 20px 44px rgba(8,5,20,0.20), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 34px rgba(255,224,130,0.08)'
              : '0 18px 40px rgba(8,5,20,0.18), 0 0 0 1px rgba(255,255,255,0.03) inset',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        opacity: isOptimistic ? 0.96 : 1,
        transform: `translateY(${interactiveLift}px) scale(${isCardPressed ? 0.996 : 1})`,
        transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, border-color 180ms ease, opacity 220ms ease, filter 220ms ease',
        zIndex: isCardHovered || viewerOpen ? 4 : 1,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: isMobile ? 20 : 26,
          background:
            'radial-gradient(260px 130px at 0% 0%, rgba(255,224,130,0.10), rgba(255,224,130,0) 58%), radial-gradient(240px 120px at 100% 0%, rgba(170,124,255,0.12), rgba(170,124,255,0) 58%), linear-gradient(180deg, rgba(255,255,255,0.018), rgba(255,255,255,0))',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: isMobile ? 14 : 18,
          right: isMobile ? 14 : 18,
          height: 1,
          background: 'linear-gradient(90deg, rgba(255,224,130,0), rgba(255,224,130,0.28), rgba(170,124,255,0.16), rgba(255,224,130,0))',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '-38%',
          width: '38%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)',
          transform: isCardHovered && !isMobile ? 'translateX(370%)' : 'translateX(0%)',
          transition: 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease',
          opacity: isCardHovered && !isMobile ? 1 : 0,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {isGlowActive ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: isMobile ? 20 : 26,
            border: isDeepLinked
              ? '1px solid rgba(139,92,246,0.24)'
              : '1px solid rgba(255,215,0,0.18)',
            boxShadow: isDeepLinked
              ? '0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 26px rgba(139,92,246,0.16), 0 0 42px rgba(255,215,0,0.08)'
              : '0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 26px rgba(255,215,0,0.14), 0 0 36px rgba(255,224,130,0.08)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      ) : null}
      {isOptimistic ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: isMobile ? 20 : 26,
            background:
              'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.04) 22%, rgba(255,215,0,0.09) 45%, rgba(255,255,255,0.04) 62%, transparent 100%)',
            transform: 'translateX(-120%)',
            animation: 'feed-post-optimistic-sheen 2.8s ease-in-out infinite',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ) : null}

      <div
        className="feed-post-card__header"
        onClick={!isThreadView ? handleOpenThread : undefined}
        style={{
          position: 'relative',
          zIndex: 2,
          padding: isMobile ? '14px 14px 8px' : '18px 18px 10px',
          cursor: !isThreadView && !viewerOpen ? 'pointer' : 'default',
          transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease',
          transform: isCardHovered && !isMobile ? 'translateY(-1px)' : 'translateY(0px)',
        }}
      >
        <div className="feed-post-card__author-block" style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 11 : 14 }}>
          <button
            type="button"
            className="feed-post-card__avatar"
            onClick={handleOpenAuthorProfile}
            aria-label={`Abrir perfil de ${author?.fullName || author?.username || 'usuario'}`}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              padding: 0,
              width: isMobile ? 44 : 52,
              height: isMobile ? 44 : 52,
              borderRadius: isMobile ? 14 : 17,
              flex: '0 0 auto',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: avatarUrl
                ? 'linear-gradient(135deg, rgba(255,231,154,0.96), rgba(255,214,106,0.98), rgba(255,192,80,0.94))'
                : `linear-gradient(135deg, ${avatarPalette.from}, ${avatarPalette.to})`,
              color: avatarUrl ? '#3A1800' : '#FFFFFF',
              fontWeight: 1000,
              fontSize: isMobile ? 16 : 18,
              border: '1px solid rgba(255,224,130,0.58)',
              boxShadow: avatarUrl
                ? '0 10px 22px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.20) inset, 0 0 0 1px rgba(255,224,130,0.30), 0 0 10px rgba(255,224,130,0.28)'
                : `0 10px 22px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.14) inset, 0 0 0 1px ${avatarPalette.accent}, 0 0 14px rgba(139,92,246,0.10)`,
             animation: (isOptimistic || isDeepLinked || isRecentlyAppliedFx)
  ? 'feed-avatar-gold-pulse 2.8s ease-in-out infinite'
  : 'none',
              cursor: authorProfileId ? 'pointer' : 'default',
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={author?.fullName || author?.username || 'Usuario'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <span
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 1000,
                  letterSpacing: 0.4,
                  textShadow: '0 2px 10px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(circle at 28% 24%, ${avatarPalette.accent}, transparent 42%), linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02))`,
                    pointerEvents: 'none',
                  }}
                />
                <span
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    fontSize: isMobile ? 14 : 16,
                  }}
                >
                  {getInitials(safePost)}
                </span>
              </span>
            )}
          </button>

          <div className="feed-post-card__author-meta" style={{ minWidth: 0, flex: 1 }}>
            <div
              className="feed-post-card__author-row"
              style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', rowGap: 6 }}
            >
              <button
                type="button"
                className="feed-post-card__author-name"
                onClick={handleOpenAuthorProfile}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  color: '#FFF2C2',
                  fontWeight: 1000,
                  fontSize: isMobile ? 15.2 : 16.8,
                  letterSpacing: -0.22,
                  textShadow: '0 6px 18px rgba(0,0,0,0.14)',
                  cursor: authorProfileId ? 'pointer' : 'default',
                }}
              >
                {author?.fullName || author?.username || 'Usuario'}
              </button>

              {author?.zodiacSign ? (
                <FeedSignBadge
                  sign={author.zodiacSign}
                  className="feed-sign-badge--compact"
                />
              ) : null}

              {isOptimistic ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 9px',
                    borderRadius: 999,
                    background: 'rgba(255,215,0,0.10)',
                    border: '1px solid rgba(255,215,0,0.16)',
                    color: '#FFE082',
                    fontSize: 11,
                    fontWeight: 1000,
                    letterSpacing: 0.2,
                    textTransform: 'uppercase',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: '#FFD700',
                      boxShadow: '0 0 10px rgba(255,215,0,0.50)',
                    }}
                  />
                  Enviando
                </span>
              ) : null}


              {isRecentlyAppliedFx && !isDeepLinked ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 9px',
                    borderRadius: 999,
                    background: 'rgba(255,215,0,0.12)',
                    border: '1px solid rgba(255,215,0,0.18)',
                    color: '#FFF1C7',
                    fontSize: 11,
                    fontWeight: 1000,
                    letterSpacing: 0.2,
                    textTransform: 'uppercase',
                    boxShadow: '0 0 18px rgba(255,215,0,0.10)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: '#FFD700',
                      boxShadow: '0 0 10px rgba(255,215,0,0.56)',
                    }}
                  />
                  {localizedValue({ es: 'Nuevo', en: 'New' })}
                </span>
              ) : null}
            </div>

            <div
              className="feed-post-card__subline"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginTop: 5,
              }}
            >
              {author?.username ? (
                <span
                  className="feed-post-card__username"
                  style={{
                    color: 'rgba(255,244,224,0.62)',
                    fontSize: isMobile ? 11 : 11.5,
                    fontWeight: 800,
                    letterSpacing: 0.08,
                  }}
                >
                  @{author.username}
                </span>
              ) : null}

              {safePost?.createdAt ? (
                <span
                  className="feed-post-card__date"
                  title={new Date(safePost.createdAt).toLocaleString('es-AR')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: 'rgba(247,242,255,0.66)',
                    fontSize: isMobile ? 11 : 11.5,
                    fontWeight: 900,
                    padding: isMobile ? '3px 8px' : '4px 9px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: 999,
                      background: 'rgba(255,215,0,0.72)',
                      boxShadow: '0 0 10px rgba(255,215,0,0.42)',
                    }}
                  />
                  {formatRelativeDate(safePost.createdAt)}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div
        className="feed-post-card__body"
        onClick={!isThreadView ? handleOpenThread : undefined}
        onKeyDown={!isThreadView ? (event) => {
          const tag = String(event?.target?.tagName || '').toLowerCase();
          if (viewerOpen) return;
          if (tag === 'button' || tag === 'img' || tag === 'input' || tag === 'textarea') return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpenThread();
          }
        } : undefined}
        style={{
          position: 'relative',
          zIndex: 2,
          padding: isMobile ? '2px 14px 12px' : '2px 18px 16px',
          cursor: !isThreadView && !viewerOpen ? 'pointer' : 'default',
        }}
      >
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: isMobile ? 16 : 22,
            background:
              isMobile
                ? 'rgba(255,255,255,0.035)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.045))',
            border: isMobile
              ? '1px solid rgba(255,255,255,0.06)'
              : '1px solid rgba(255,224,130,0.08)',
            padding: isMobile ? '13px 12px 12px' : '17px 16px 16px',
            boxShadow: isMobile
              ? 'none'
              : isCardHovered
                ? '0 10px 24px rgba(0,0,0,0.08) inset, 0 0 0 1px rgba(255,255,255,0.025) inset, 0 0 24px rgba(255,224,130,0.05)'
                : '0 10px 24px rgba(0,0,0,0.08) inset, 0 0 0 1px rgba(255,255,255,0.025) inset',
            transform: isCardPressed ? 'scale(0.998)' : 'scale(1)',
            transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, border-color 180ms ease',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'linear-gradient(135deg, rgba(255,224,130,0.07), rgba(255,255,255,0.015) 30%, rgba(170,124,255,0.07) 68%, rgba(255,224,130,0.04))',
              opacity: isCardHovered && !isMobile ? 1 : 0.72,
              transition: 'opacity 180ms ease',
            }}
          />
          {safePost?.text ? (
            <p
              className="feed-post-card__text"
              style={{
                position: 'relative',
                zIndex: 1,
                margin: 0,
                color: '#FFFDFC',
                fontSize: isMobile ? 15 : 15.7,
                lineHeight: isMobile ? 1.62 : 1.74,
                fontWeight: 500,
                letterSpacing: 0.04,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                textShadow: '0 2px 10px rgba(0,0,0,0.10)',
              }}
            >
              {safePost.text}
            </p>
          ) : null}

          {hasImageMedia ? (
            <div
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              style={{
                position: 'relative',
                zIndex: 1,
                marginTop: safePost?.text ? 12 : 0,
              }}
            >
              <FeedPostImage
                post={safePost}
                image={safePost?.media?.image || {}}
                isMobile={isMobile}
                onOpen={handleOpenImageViewer}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div
        className="feed-post-card__actions"
        style={{
          position: 'relative',
          zIndex: 40,
          padding: isMobile ? '0 14px 8px' : '0 18px 10px',
          marginTop: 0,
          overflow: 'visible',
        }}
      >
        <div
          style={{
            position: 'relative',
            overflow: 'visible',
            zIndex: 50,
            borderRadius: isMobile ? 16 : 20,
            border: isMobile ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,224,130,0.12)',
            background:
              isMobile
                ? 'rgba(255,255,255,0.035)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))',
            padding: isMobile ? '7px 8px' : '9px',
            boxShadow: isMobile
              ? 'none'
              : isCardHovered
                ? '0 10px 22px rgba(0,0,0,0.07) inset, 0 0 24px rgba(255,224,130,0.05)'
                : '0 10px 22px rgba(0,0,0,0.07) inset',
            transform: isCardHovered && !isMobile ? 'translateY(-1px)' : 'translateY(0px)',
            transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, border-color 180ms ease',
            isolation: 'isolate',
          }}
        >
          <FeedPostActions
            post={safePost}
            onLike={handleLike}
            onComment={showInlineComments ? handleToggleComments : handleOpenThread}
            onDelete={handleDelete}
            liking={liking}
            deleting={deleting}
            commentCountOverride={displayedCommentCount}
          />
        </div>
      </div>

      {showInlineComments && ((commentsOpen && (commentsLoading || commentsCreating)) || (displayedCommentCount > 0 && !commentsOpen)) ? (
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            padding: isMobile ? '0 14px 12px' : '0 18px 14px',
            flexWrap: 'wrap',
          }}
        >
          {commentsOpen && (commentsLoading || commentsCreating) ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                color: 'rgba(247,242,255,0.66)',
                fontSize: 11.5,
                fontWeight: 800,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: '#FFD700',
                  boxShadow: '0 0 10px rgba(255,215,0,0.42)',
                  animation: 'feed-post-loading-pulse 1.3s ease-in-out infinite',
                }}
              />
              {commentsCreating ? 'Enviando...' : commentsLoading ? 'Cargando...' : ''}
            </div>
          ) : null}

          {displayedCommentCount > 0 && !commentsOpen ? (
            <span
              style={{
                color: 'rgba(247,242,255,0.62)',
                fontSize: 11.5,
                fontWeight: 800,
                letterSpacing: 0.04,
              }}
            >
              {displayedCommentCount === 1 ? '1 respuesta' : `${displayedCommentCount} respuestas`}
            </span>
          ) : null}
        </div>
      ) : null}

      {showInlineComments ? (
        <div
          className={`feed-post-card__comments-wrap ${commentsOpen ? 'is-open' : 'is-closed'} ${isThreadView && commentsOpen ? 'is-thread-focus-open' : ''}`.trim()}
          style={{
            position: 'relative',
            zIndex: 0,
            display: 'grid',
            gridTemplateRows: commentsOpen ? '1fr' : '0fr',
            opacity: commentsOpen ? 1 : 0,
            transition: 'grid-template-rows 300ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease',
          }}
        >
          <div style={{ minHeight: 0, overflow: commentsOpen ? 'visible' : 'hidden' }}>
            <div
              className="feed-post-card__comments"
              style={{
                padding: isMobile ? '0 14px 14px' : '0 18px 18px',
                marginTop: -1,
                transform: commentsOpen ? 'translateY(0px) scale(1)' : 'translateY(-10px) scale(0.988)',
                transformOrigin: 'top center',
                transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 210ms ease',
                opacity: commentsOpen ? 1 : 0,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  paddingTop: 12,
                  opacity: 0.98,
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -1,
                    left: 0,
                    right: 0,
                    height: 1,
                    background: 'linear-gradient(90deg, rgba(255,224,130,0), rgba(255,224,130,0.36), rgba(170,124,255,0.16), rgba(255,224,130,0))',
                    pointerEvents: 'none',
                  }}
                />

                <div
                  style={{
                    borderRadius: isMobile ? 16 : 22,
                    border: isThreadView && commentsOpen
                      ? '1px solid rgba(255,224,130,0.12)'
                      : '1px solid rgba(255,255,255,0.07)',
                    background: isThreadView && commentsOpen
                      ? 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.028))'
                      : 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.025))',
                    boxShadow: isMobile
                      ? 'none'
                      : isThreadView && commentsOpen
                        ? '0 12px 24px rgba(0,0,0,0.08) inset, 0 0 0 1px rgba(255,255,255,0.02) inset, 0 0 24px rgba(255,224,130,0.05)'
                        : '0 12px 24px rgba(0,0,0,0.08) inset, 0 0 0 1px rgba(255,255,255,0.02) inset',
                    padding: isMobile ? 10 : 12,
                    transition: 'border-color 220ms ease, box-shadow 220ms ease, background 220ms ease',
                  }}
                >
                <FeedCommentList
                  post={safePost}
                  comments={comments}
                  loading={commentsLoading}
                  creating={commentsCreating}
                  error={commentsError}
                  onSubmitComment={handleSubmitComment}
                  onDeleteComment={handleDeleteComment}
                  onLikeComment={handleLikeComment}
                  onReportComment={handleReportComment}
                />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <FeedImageViewer
        open={viewerOpen}
        image={viewerImage}
        isMobile={isMobile}
        onClose={handleCloseImageViewer}
      />

      <style>{`
      @keyframes feed-avatar-gold-pulse {
        0% {
          box-shadow:
            0 14px 28px rgba(0,0,0,0.24),
            0 0 0 1px rgba(255,255,255,0.25) inset,
            0 0 0 1px rgba(255,215,0,0.35),
            0 0 12px rgba(255,215,0,0.45),
            0 0 28px rgba(255,215,0,0.25);
        }
        50% {
          box-shadow:
            0 14px 28px rgba(0,0,0,0.24),
            0 0 0 1px rgba(255,255,255,0.26) inset,
            0 0 0 1px rgba(255,215,0,0.50),
            0 0 16px rgba(255,215,0,0.60),
            0 0 36px rgba(255,215,0,0.34);
        }
        100% {
          box-shadow:
            0 14px 28px rgba(0,0,0,0.24),
            0 0 0 1px rgba(255,255,255,0.25) inset,
            0 0 0 1px rgba(255,215,0,0.35),
            0 0 12px rgba(255,215,0,0.45),
            0 0 28px rgba(255,215,0,0.25);
        }
      }
      @keyframes feed-post-loading-pulse {
        0% { transform: scale(0.95); opacity: 0.72; }
        50% { transform: scale(1.08); opacity: 1; }
        100% { transform: scale(0.95); opacity: 0.72; }
      }
      @keyframes feed-post-optimistic-sheen {
        0% { transform: translateX(-120%); opacity: 0; }
        25% { opacity: 0.45; }
        60% { opacity: 0.18; }
        100% { transform: translateX(120%); opacity: 0; }
      }
      @keyframes feed-post-deep-link-glow {
        0% {
          box-shadow:
            0 34px 72px rgba(0,0,0,0.34),
            0 0 0 1px rgba(255,255,255,0.05) inset,
            0 0 68px rgba(139,92,246,0.22),
            0 0 30px rgba(255,215,0,0.14);
        }
        50% {
          box-shadow:
            0 34px 72px rgba(0,0,0,0.34),
            0 0 0 1px rgba(255,255,255,0.05) inset,
            0 0 82px rgba(139,92,246,0.28),
            0 0 38px rgba(255,215,0,0.18);
        }
        100% {
          box-shadow:
            0 30px 68px rgba(0,0,0,0.32),
            0 0 0 1px rgba(255,255,255,0.05) inset,
            0 0 58px rgba(139,92,246,0.16),
            0 0 24px rgba(255,215,0,0.10);
        }
      }

      @keyframes feed-post-recent-glow {
        0% {
          box-shadow:
            0 34px 72px rgba(0,0,0,0.34),
            0 0 0 1px rgba(255,255,255,0.05) inset,
            0 0 62px rgba(255,215,0,0.20),
            0 0 28px rgba(255,224,130,0.14);
        }
        50% {
          box-shadow:
            0 34px 72px rgba(0,0,0,0.34),
            0 0 0 1px rgba(255,255,255,0.05) inset,
            0 0 78px rgba(255,215,0,0.28),
            0 0 36px rgba(255,224,130,0.18);
        }
        100% {
          box-shadow:
            0 30px 68px rgba(0,0,0,0.32),
            0 0 0 1px rgba(255,255,255,0.05) inset,
            0 0 46px rgba(255,215,0,0.16),
            0 0 20px rgba(255,224,130,0.10);
        }
      }
      .feed-sign-badge--compact {
        transform: scale(0.85);
        opacity: 0.85;
        padding: 2px 6px !important;
        border-radius: 10px !important;
      }
      .feed-sign-badge--compact .feed-sign-badge__symbol {
        font-size: 12px !important;
      }
      .feed-sign-badge--compact .feed-sign-badge__label {
        font-size: 11px !important;
        font-weight: 700 !important;
      }
      .feed-post-card {
        transition: transform 190ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 190ms ease, border-color 190ms ease, opacity 240ms ease, filter 220ms ease;
        transform: translateY(16px) scale(0.985);
        opacity: 0;
        will-change: transform, box-shadow, opacity, filter;
      }
      .feed-post-card.is-mounted-in {
        transform: translateY(0px) scale(1);
        opacity: 1;
      }
      .feed-post-card.is-thread-focus-open {
        box-shadow:
          0 28px 62px rgba(8,5,20,0.26),
          0 0 0 1px rgba(255,255,255,0.04) inset,
          0 0 40px rgba(255,224,130,0.08),
          0 0 22px rgba(170,124,255,0.06);
      }
      .feed-post-card:hover {
        transform: translateY(-1px);
        box-shadow:
          0 22px 46px rgba(8,5,20,0.20),
          0 0 0 1px rgba(255,255,255,0.04) inset,
          0 0 30px rgba(255,224,130,0.08);
      }

      .feed-post-card:focus-within {
        box-shadow:
          0 22px 46px rgba(8,5,20,0.22),
          0 0 0 1px rgba(255,255,255,0.04) inset,
          0 0 34px rgba(255,224,130,0.10),
          0 0 0 2px rgba(255,224,130,0.10);
      }
      .feed-post-card.is-hovered {
        z-index: 4;
      }
      .feed-post-card.is-pressed {
        transition-duration: 120ms;
      }
      @media (max-width: 640px) {
        .feed-post-card {
          border-radius: 20px;
        }
      }
      .feed-post-card.is-deep-linked {
        animation: feed-post-deep-link-glow 3.4s ease-in-out 1;
      }
      .feed-post-card.is-recently-applied {
        animation: feed-post-recent-glow 3.2s ease-in-out 1;
      }
      .feed-post-card.is-optimistic {
        border-color: rgba(255,215,0,0.22);
      }
      .feed-post-card.is-optimistic:hover {
        box-shadow:
          0 30px 64px rgba(8,5,20,0.24),
          0 0 0 1px rgba(255,255,255,0.04) inset,
          0 0 52px rgba(255,224,130,0.14);
      }
      .feed-post-card__comments-wrap {
        will-change: grid-template-rows, opacity;
      }
      .feed-post-card__actions,
      .feed-post-card__actions > div {
        overflow: visible !important;
      }

      .feed-post-card__comments-wrap.is-thread-focus-open {
        filter: saturate(1.03);
      }

      .feed-post-card__comments {
        will-change: transform, opacity;
      }
      @media (prefers-reduced-motion: reduce) {
  .feed-post-card,
  .feed-post-card__comments-wrap,
  .feed-post-card__comments,
  .feed-post-card__avatar,
  .feed-post-card__header,
  .feed-post-card__body {
    animation: none !important;
    transition: none !important;
  }

  .feed-post-card__avatar {
    box-shadow:
      0 10px 22px rgba(0,0,0,0.18),
      0 0 0 1px rgba(255,255,255,0.20) inset,
      0 0 0 1px rgba(255,224,130,0.30),
      0 0 10px rgba(255,224,130,0.28) !important;
  }
}
    `}</style>
    </article>
  );
}
